/**
 * Storage Management Utilities
 * Helps prevent and handle localStorage quota exceeded errors
 */

// Storage size limits (browsers vary, but these are safe estimates)
const STORAGE_LIMITS = {
  WARNING_THRESHOLD: 4 * 1024 * 1024, // 4MB - warn at this point
  MAX_ITEM_SIZE: 1024 * 1024, // 1MB max per item
  CLEANUP_TARGET: 2 * 1024 * 1024, // Clean down to 2MB when quota exceeded
};

/**
 * Get current localStorage usage information
 */
export const getStorageInfo = () => {
  let totalSize = 0;
  let itemCount = 0;
  const items = {};

  for (let key in localStorage) {
    if (localStorage.hasOwnProperty(key)) {
      const size = localStorage[key].length;
      totalSize += size;
      itemCount++;
      items[key] = size;
    }
  }

  return {
    totalSize,
    itemCount,
    items,
    isNearLimit: totalSize > STORAGE_LIMITS.WARNING_THRESHOLD,
    availableSpace: Math.max(0, 5 * 1024 * 1024 - totalSize), // Assume 5MB limit
  };
};

/**
 * Check if localStorage has enough space for data
 */
export const hasStorageSpace = (dataSize) => {
  const info = getStorageInfo();
  return info.availableSpace > dataSize;
};

/**
 * Safe localStorage setter with automatic cleanup
 */
export const safeSetItem = (key, value, options = {}) => {
  const {
    compress = false,
    maxRetries = 2,
    cleanupOnFail = true,
    maxSize = STORAGE_LIMITS.MAX_ITEM_SIZE,
  } = options;

  const setValue = (data, attempt = 1) => {
    try {
      const jsonString = JSON.stringify(data);
      
      if (jsonString.length > maxSize) {
        console.warn(`Data for ${key} exceeds size limit (${jsonString.length} bytes)`);
        if (compress && typeof options.compressor === 'function') {
          const compressed = options.compressor(data);
          return setValue(compressed, attempt);
        }
        return { success: false, error: 'Data too large', size: jsonString.length };
      }

      localStorage.setItem(key, jsonString);
      return { success: true, size: jsonString.length };
      
    } catch (error) {
      if ((error.name === 'QuotaExceededError' || error.code === 22) && attempt <= maxRetries) {
        console.warn(`Storage quota exceeded for ${key}, attempt ${attempt}/${maxRetries}`);
        
        if (cleanupOnFail) {
          const cleaned = cleanupStorage(STORAGE_LIMITS.CLEANUP_TARGET);
          
          if (attempt < maxRetries) {
            return setValue(data, attempt + 1);
          }
        }
      }
      
      return { 
        success: false, 
        error: error.name || error.message, 
        code: error.code 
      };
    }
  };

  return setValue(value);
};

/**
 * Safe localStorage getter with error handling
 */
export const safeGetItem = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key);
    if (item === null) return defaultValue;
    
    return JSON.parse(item);
  } catch (error) {
    console.warn(`Error parsing localStorage item ${key}:`, error);
    return defaultValue;
  }
};

/**
 * Clean up localStorage by removing old or large items
 */
export const cleanupStorage = (targetSize = STORAGE_LIMITS.CLEANUP_TARGET) => {
  const info = getStorageInfo();
  let currentSize = info.totalSize;
  let itemsRemoved = 0;
  let spaceFreed = 0;

  // Priority order for cleanup (remove these first)
  const cleanupPriority = [
    // Old flight data
    'oldFlightsByPrice',
    'oldFlightsByQuality', 
    'oldFlightsByDuration',
    // Temporary data
    'ancillaryData',
    'preciseSearchData',
    // Cache items (identify by common patterns)
    /^cache_/,
    /^temp_/,
    /^api_cache_/,
    // Large items (sort by size)
  ];

  // Remove items by priority
  for (const pattern of cleanupPriority) {
    if (currentSize <= targetSize) break;

    for (const key in info.items) {
      if (currentSize <= targetSize) break;

      const matches = typeof pattern === 'string' 
        ? key === pattern 
        : pattern.test(key);

      if (matches) {
        try {
          const size = info.items[key];
          localStorage.removeItem(key);
          currentSize -= size;
          spaceFreed += size;
          itemsRemoved++;
        } catch (e) {
          console.warn(`Could not remove ${key}:`, e);
        }
      }
    }
  }

  // If still over target, remove largest items
  if (currentSize > targetSize) {
    const sortedItems = Object.entries(info.items)
      .sort(([,a], [,b]) => b - a) // Sort by size, largest first
      .filter(([key]) => !key.startsWith('search')); // Preserve search data

    for (const [key, size] of sortedItems) {
      if (currentSize <= targetSize) break;
      
      try {
        localStorage.removeItem(key);
        currentSize -= size;
        spaceFreed += size;
        itemsRemoved++;
      } catch (e) {
        console.warn(`Could not remove ${key}:`, e);
      }
    }
  }

  return {
    itemsRemoved,
    spaceFreed,
    remainingSize: currentSize,
    success: currentSize <= targetSize,
  };
};

/**
 * Monitor storage usage and warn if approaching limits
 */
export const monitorStorage = () => {
  const info = getStorageInfo();
  
  if (info.isNearLimit) {
    console.warn(`localStorage usage is high: ${(info.totalSize / 1024).toFixed(2)}KB`);
  }

  return info;
};

/**
 * Clear all flight-related data from localStorage
 */
export const clearFlightData = () => {
  const flightDataKeys = [
    'flightsByPrice',
    'flightsByQuality',
    'flightsByDuration',
    'oldFlightsByPrice',
    'oldFlightsByQuality',
    'oldFlightsByDuration',
    'ancillaryData',
    'currentBooking',
    'preciseSearchData',
  ];

  let cleared = 0;
  flightDataKeys.forEach(key => {
    try {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
        cleared++;
      }
    } catch (e) {
      console.warn(`Could not clear ${key}:`, e);
    }
  });

  return cleared;
};

/**
 * Format bytes to human readable format
 */
export const formatBytes = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export default {
  getStorageInfo,
  hasStorageSpace,
  safeSetItem,
  safeGetItem,
  cleanupStorage,
  monitorStorage,
  clearFlightData,
  formatBytes,
}; 